import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export class ApiRequestError extends Error {
  status?: number;
  data?: unknown;
}

export class ApiClientService {
  private static readonly accessTokenKey = 'dotstorming_access_token';
  private static readonly refreshTokenKey = 'dotstorming_refresh_token';
  private static readonly defaultApiRootsBaseUrl = 'http://localhost:8082';

  private static getStorage() {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage;
  }

  private static getAccessToken() {
    return this.getStorage()?.getItem(this.accessTokenKey) ?? null;
  }

  private static getRefreshToken() {
    return this.getStorage()?.getItem(this.refreshTokenKey) ?? null;
  }

  private static setTokens(accessToken: string, refreshToken: string) {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    storage.setItem(this.accessTokenKey, accessToken);
    storage.setItem(this.refreshTokenKey, refreshToken);
  }

  private static clearTokens() {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    storage.removeItem(this.accessTokenKey);
    storage.removeItem(this.refreshTokenKey);
  }

  public static getApiBaseUrl(): string {
    const envBaseUrl =
      import.meta.env.VITE_API_ROOTS_BASE_URL ??
      import.meta.env.VITE_API_ROOTS_URL ??
      import.meta.env.VITE_API_BASE_URL;

    if (typeof envBaseUrl === 'string' && envBaseUrl.trim().length > 0) {
      return envBaseUrl.replace(/\/+$/, '');
    }

    return this.defaultApiRootsBaseUrl;
  }

  private static async refreshAccessToken(apiRoot: string): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await axios.post(`${apiRoot}/users/refreshToken`, { token: refreshToken });
      const payload = response.data;
      if (!payload?.token?.accessToken || !payload?.token?.refreshToken) {
        this.clearTokens();
        return false;
      }

      this.setTokens(payload.token.accessToken, payload.token.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  private static async authRequest<T>(
    config: AxiosRequestConfig,
    retry = true
  ): Promise<AxiosResponse<T>> {
    const accessToken = this.getAccessToken();

    try {
      return await axios.request<T>({
        ...config,
        headers: {
          ...(config.headers ?? {}),
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401 && retry) {
        const refreshed = await this.refreshAccessToken(this.getApiBaseUrl());
        if (refreshed) {
          return this.authRequest<T>(config, false);
        }
      }

      throw error;
    }
  }

  private static readData<T>(response: AxiosResponse<T>): T {
    if (response.status === 204) {
      return null as T;
    }

    return response.data;
  }

  private static toError(error: unknown): Error {
    const axiosError = error as AxiosError;
    const requestError = new ApiRequestError();
    requestError.status = axiosError.response?.status;
    requestError.data = axiosError.response?.data;

    const messageFromResponse =
      (axiosError.response?.data as { message?: string } | string | undefined) ?? '';

    if (typeof messageFromResponse === 'string' && messageFromResponse.trim().length > 0) {
      requestError.message = messageFromResponse;
      return requestError;
    }

    if (
      typeof messageFromResponse === 'object' &&
      messageFromResponse &&
      typeof messageFromResponse.message === 'string'
    ) {
      requestError.message = messageFromResponse.message;
      return requestError;
    }

    requestError.message = axiosError.message || 'Request failed';
    return requestError;
  }

  static async getJson<T>(url: string): Promise<T> {
    try {
      const response = await this.authRequest<T>({ url, method: 'GET' });
      return this.readData<T>(response);
    } catch (error) {
      throw this.toError(error);
    }
  }

  static async postJson<T>(url: string, payload: unknown): Promise<T> {
    try {
      const response = await this.authRequest<T>({
        url,
        method: 'POST',
        data: payload,
      });
      return this.readData<T>(response);
    } catch (error) {
      throw this.toError(error);
    }
  }

  static async postFormData<T>(url: string, payload: FormData): Promise<T> {
    try {
      const response = await this.authRequest<T>({
        url,
        method: 'POST',
        data: payload,
      });
      return this.readData<T>(response);
    } catch (error) {
      throw this.toError(error);
    }
  }

  static async putJson<T>(url: string, payload: unknown): Promise<T> {
    try {
      const response = await this.authRequest<T>({
        url,
        method: 'PUT',
        data: payload,
      });
      return this.readData<T>(response);
    } catch (error) {
      throw this.toError(error);
    }
  }

  static async deleteJson<T>(url: string): Promise<T> {
    try {
      const response = await this.authRequest<T>({ url, method: 'DELETE' });
      return this.readData<T>(response);
    } catch (error) {
      throw this.toError(error);
    }
  }
}
